<template>
  <div v-if="isOpen" class="cmd-overlay" @click.self="close">
    <div class="cmd-palette">
      <!-- Search Input -->
      <div class="cmd-input-wrapper">
        <span class="mdi mdi-magnify cmd-search-icon"></span>
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          class="cmd-input"
          :placeholder="$currentLocale === 'id' ? 'Cari menu, aksi cepat, atau halaman... (↑↓ navigasi, Enter pilih)' : 'Search commands, navigation, actions... (↑↓ navigate, Enter select)'"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="selectCurrent"
          @keydown.esc.prevent="close"
        />
        <button type="button" class="btn btn-xs btn-outline-secondary cmd-kbd" @click="close">
          ESC
        </button>
      </div>

      <!-- Quick Actions / Search Results List -->
      <div class="cmd-results">
        <div v-if="filteredItems.length === 0" class="cmd-empty text-muted">
          {{ $currentLocale === 'id' ? `Tidak ada menu ditemukan untuk "${query}"` : `No commands found for "${query}"` }}
        </div>
        <div
          v-for="(item, idx) in filteredItems"
          :key="item.id"
          class="cmd-item"
          :class="{ 'is-active': idx === activeIndex }"
          @mouseenter="activeIndex = idx"
          @click="executeItem(item)"
        >
          <div class="d-flex align-items-center">
            <div class="cmd-icon-box mr-3" :class="item.iconBgClass">
              <span class="mdi" :class="[item.icon, item.colorClass]"></span>
            </div>
            <div>
              <div class="cmd-item-title text-white font-weight-medium">{{ item.title }}</div>
              <div v-if="item.subtitle" class="cmd-item-sub text-muted small">{{ item.subtitle }}</div>
            </div>
          </div>
          <span class="badge badge-secondary font-weight-normal small">{{ item.category }}</span>
        </div>
      </div>

      <!-- Footer Help -->
      <div class="cmd-footer d-flex justify-content-between align-items-center">
        <span class="text-muted small">
          <kbd class="cmd-mini-kbd">↵</kbd> {{ $currentLocale === 'id' ? 'Buka' : 'Open' }} &nbsp;&nbsp;
          <kbd class="cmd-mini-kbd">↑</kbd> <kbd class="cmd-mini-kbd">↓</kbd> {{ $currentLocale === 'id' ? 'Navigasi' : 'Navigate' }} &nbsp;&nbsp;
          <kbd class="cmd-mini-kbd">esc</kbd> {{ $currentLocale === 'id' ? 'Tutup' : 'Close' }}
        </span>
        <span class="text-muted small" style="font-size: 11px;">
          Gamiteam Command Palette
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CommandPalette',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    isSuperAdmin: {
      type: Boolean,
      default: false
    },
    isMaker: {
      type: Boolean,
      default: false
    },
    isOwner: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      query: '',
      activeIndex: 0
    };
  },
  computed: {
    allItems() {
      const isId = this.$currentLocale === 'id';

      if (this.isSuperAdmin) {
        return [
          {
            id: 'sa-dashboard',
            title: isId ? 'Dashboard Operasional Superadmin' : 'Platform Operations Dashboard',
            subtitle: isId ? 'Ikhtisar MRR, pendapatan, dan telemetri platform' : 'MRR, revenue, and platform telemetry overview',
            category: isId ? 'Platform' : 'Platform',
            icon: 'mdi-speedometer',
            colorClass: 'text-info',
            iconBgClass: 'bg-subtle',
            route: '/dashboard'
          },
          {
            id: 'sa-leads',
            title: isId ? 'Inbound Leads & Registrasi Mitra' : 'Inbound Leads & Client Registrations',
            subtitle: isId ? 'Pipeline pendaftaran demo dari landing page publik' : 'Review and convert demo requests from landing page',
            category: isId ? 'Pipeline' : 'Pipeline',
            icon: 'mdi-inbox-arrow-down',
            colorClass: 'text-warning',
            iconBgClass: 'bg-subtle',
            route: '/inbound-leads'
          },
          {
            id: 'sa-benchmarks',
            title: isId ? 'Benchmark Game Setting & Blueprint' : 'Game Setting Benchmarks & Blueprints',
            subtitle: isId ? 'Telemetri efektivitas quest lintas divisi & master template' : 'Cross-tenant telemetry, division heatmaps & blueprints',
            category: isId ? 'Platform' : 'Platform',
            icon: 'mdi-chart-bell-curve-cumulative',
            colorClass: 'text-info',
            iconBgClass: 'bg-subtle',
            route: '/game-benchmarks'
          },
          {
            id: 'sa-creators',
            title: isId ? 'Ekosistem Kreator & Bagi Hasil' : 'Creator Ecosystem & Revenue Share',
            subtitle: isId ? 'Leaderboard kreator, pendaftaran partner, dan komisi 70/30' : 'Creator rankings, partner applications, and 70/30 split',
            category: isId ? 'Komunitas' : 'Community',
            icon: 'mdi-account-star',
            colorClass: 'text-success',
            iconBgClass: 'bg-subtle',
            route: '/creators'
          },
          {
            id: 'sa-onboard',
            title: isId ? 'Daftarkan Perusahaan Mitra Baru' : 'Onboard New Client Enterprise',
            subtitle: isId ? 'Registrasi mitra baru dan alokasikan kuota pemain' : 'Register new tenant and provision seat quota',
            category: isId ? 'Mitra' : 'Tenants',
            icon: 'mdi-plus-circle-outline',
            colorClass: 'text-primary',
            iconBgClass: 'bg-subtle',
            route: '/owner/add'
          },
          {
            id: 'sa-owners',
            title: isId ? 'Daftar Seluruh Perusahaan Mitra' : 'All Client Enterprises (Tenants)',
            subtitle: isId ? 'Kelola seluruh perusahaan klien di platform' : 'Manage all client organizations on Gamiteam',
            category: isId ? 'Mitra' : 'Tenants',
            icon: 'mdi-domain',
            colorClass: 'text-warning',
            iconBgClass: 'bg-subtle',
            route: '/owner'
          },
          {
            id: 'sa-billing',
            title: isId ? 'Verifikasi Invoice & Pembayaran' : 'Verify Invoice Approvals',
            subtitle: isId ? 'Konfirmasi bukti transfer bank manual dari klien' : 'Review manual bank transfer payments',
            category: isId ? 'Keuangan' : 'Billing',
            icon: 'mdi-cash-check',
            colorClass: 'text-success',
            iconBgClass: 'bg-subtle',
            route: '/billing'
          },
          {
            id: 'sa-packages',
            title: isId ? 'Paket Langganan SaaS' : 'Subscription Plans & Tiers',
            subtitle: isId ? 'Atur harga per kursi, kuota, dan fitur paket' : 'Configure seat pricing, quotas, and perks',
            category: isId ? 'Keuangan' : 'Billing',
            icon: 'mdi-package-variant',
            colorClass: 'text-primary',
            iconBgClass: 'bg-subtle',
            route: '/package'
          },
          {
            id: 'sa-discounts',
            title: isId ? 'Kupon & Voucher Diskon' : 'Discount Promo Codes',
            subtitle: isId ? 'Kelola voucher promo dan potongan harga klien' : 'Manage promo codes and percentage discounts',
            category: isId ? 'Keuangan' : 'Billing',
            icon: 'mdi-ticket-percent',
            colorClass: 'text-danger',
            iconBgClass: 'bg-subtle',
            route: '/discount'
          },
          {
            id: 'sa-players',
            title: isId ? 'Direktori Karyawan (Global Players)' : 'Global Platform Players Directory',
            subtitle: isId ? 'Seluruh karyawan lintas perusahaan mitra' : 'All employees participating across client tenants',
            category: isId ? 'Pengguna' : 'Users',
            icon: 'mdi-account-group',
            colorClass: 'text-info',
            iconBgClass: 'bg-subtle',
            route: '/adminplayer'
          },
          {
            id: 'sa-makers',
            title: isId ? 'Direktori Manajer (Global Makers)' : 'Global Platform Managers Directory',
            subtitle: isId ? 'Seluruh team lead dan manajer quest' : 'All operational leaders and quest creators',
            category: isId ? 'Pengguna' : 'Users',
            icon: 'mdi-shield-account',
            colorClass: 'text-success',
            iconBgClass: 'bg-subtle',
            route: '/adminmaker'
          },
          {
            id: 'sa-realms',
            title: isId ? 'Marketplace Dunia Virtual 3D' : 'Virtual Realms 3D Marketplace',
            subtitle: isId ? 'Katalog dunia virtual dan lingkungan 3D interaktif' : 'Interactive 3D virtual realms and environments catalog',
            category: isId ? 'Platform' : 'Platform',
            icon: 'mdi-gamepad-variant',
            colorClass: 'text-warning',
            iconBgClass: 'bg-subtle',
            route: '/realms/marketplace'
          },
          {
            id: 'sa-glossary',
            title: isId ? 'Glosarium & Panduan Gamifikasi' : 'Gamification Glossary & Guide',
            subtitle: isId ? 'Kamus istilah RPG dan metrik HR korporat' : 'RPG terminology dictionary and corporate HR metrics',
            category: isId ? 'Panduan' : 'Guide',
            icon: 'mdi-book-open-page-variant',
            colorClass: 'text-info',
            iconBgClass: 'bg-subtle',
            route: '/glossary'
          },
          {
            id: 'sa-audit',
            title: isId ? 'Audit Log Keamanan & Sistem' : 'System & Security Audit Logs',
            subtitle: isId ? 'Catatan riwayat aksi admin dan keamanan' : 'Track platform security events and administrative actions',
            category: isId ? 'Sistem' : 'System',
            icon: 'mdi-shield-check',
            colorClass: 'text-warning',
            iconBgClass: 'bg-subtle',
            route: '/audit-logs'
          }
        ];
      }

      // Client Enterprise (Owner / Maker)
      const items = [
        {
          id: 'ent-dashboard',
          title: isId ? 'Dashboard Workspace Perusahaan' : 'Company Workspace Dashboard',
          subtitle: isId ? 'Ringkasan performa tim, level, dan ekonomi gold' : 'Team engagement, leveling, and gold economy',
          category: isId ? 'Workspace' : 'Workspace',
          icon: 'mdi-speedometer',
          colorClass: 'text-primary',
          iconBgClass: 'bg-subtle',
          route: '/dashboard'
        },
        ...(this.isOwner && !this.isMaker ? [
          {
            id: 'ent-owner-analytics',
            title: isId ? 'Analitik Eksekutif & ROI' : 'Executive ROI & Burnout Analytics',
            subtitle: isId ? 'Deteksi risiko burnout, ROI produktivitas, dan progres boss' : 'Burnout radar, productivity ROI, and boss raid telemetry',
            category: isId ? 'Eksekutif' : 'Executive',
            icon: 'mdi-finance',
            colorClass: 'text-success',
            iconBgClass: 'bg-subtle',
            route: '/owner/analytics'
          }
        ] : []),
        {
          id: 'ent-maker-studio',
          title: isId ? 'Studio Operasional Game Maker' : 'Game Maker Operations Studio',
          subtitle: isId ? 'Rancang quest AI, verifikasi kilat, dan kelola squad' : 'Design AI quests, rapid batch verification, and squad tools',
          category: isId ? 'Studio' : 'Studio',
          icon: 'mdi-auto-fix',
          colorClass: 'text-info',
          iconBgClass: 'bg-subtle',
          route: '/maker/studio'
        },
        {
          id: 'ent-quests',
          title: isId ? 'Quests & Target Kerja' : 'Quests & Performance Targets',
          subtitle: isId ? 'Kelola misi kerja harian, mingguan, dan sprint' : 'Manage daily, weekly, and sprint target quests',
          category: isId ? 'Gamifikasi' : 'Gamification',
          icon: 'mdi-trophy-variant',
          colorClass: 'text-warning',
          iconBgClass: 'bg-subtle',
          route: '/rules/rewards'
        },
        {
          id: 'ent-approvals',
          title: isId ? 'Pusat Verifikasi Pengajuan Misi' : 'Quest Submissions Approval Center',
          subtitle: isId ? 'Review bukti pekerjaan karyawan dan berikan XP & Gold' : 'Verify employee work proofs and grant XP & Gold',
          category: isId ? 'Verifikasi' : 'Approvals',
          icon: 'mdi-check-decagram',
          colorClass: 'text-success',
          iconBgClass: 'bg-subtle',
          route: '/approval/rewards'
        },
        {
          id: 'ent-consequences',
          title: isId ? 'Tata Tertib SOP & Penalti' : 'SOP Rules & Penalties',
          subtitle: isId ? 'Aturan pelanggaran SOP dan pengurangan poin' : 'Disciplinary SOP rules and point deductions',
          category: isId ? 'Gamifikasi' : 'Gamification',
          icon: 'mdi-alert-circle',
          colorClass: 'text-danger',
          iconBgClass: 'bg-subtle',
          route: '/rules/consequences'
        },
        {
          id: 'ent-compensations',
          title: isId ? 'Tugas Penebusan Penalti' : 'Penalty Compensation Tasks',
          subtitle: isId ? 'Tugas ekstra pemulihan nilai penalti tim' : 'Extra tasks to redeem disciplinary strikes',
          category: isId ? 'Gamifikasi' : 'Gamification',
          icon: 'mdi-restore',
          colorClass: 'text-info',
          iconBgClass: 'bg-subtle',
          route: '/rules/compensations'
        },
        {
          id: 'ent-squads',
          title: isId ? 'Squads & Divisi Perusahaan' : 'Company Squads & Divisions',
          subtitle: isId ? 'Atur pembagian tim, squad lead, dan anggota' : 'Organize teams, squad leads, and player members',
          category: isId ? 'Organisasi' : 'Organization',
          icon: 'mdi-account-group',
          colorClass: 'text-primary',
          iconBgClass: 'bg-subtle',
          route: '/squad'
        },
        {
          id: 'ent-players',
          title: isId ? 'Direktori Pemain (Game Players)' : 'Company Players Directory',
          subtitle: isId ? 'Kelola akun karyawan, poin XP, dan saldo Gold' : 'Manage employee accounts, XP points, and Gold balances',
          category: isId ? 'Personel' : 'Personnel',
          icon: 'mdi-account-multiple',
          colorClass: 'text-info',
          iconBgClass: 'bg-subtle',
          route: '/player'
        },
        ...(this.isOwner && !this.isMaker ? [
          {
            id: 'ent-makers',
            title: isId ? 'Direktori Manajer (Game Makers)' : 'Company Game Makers Directory',
            subtitle: isId ? 'Kelola akun team lead dan kreator misi perusahaan' : 'Manage team leads and quest creators',
            category: isId ? 'Personel' : 'Personnel',
            icon: 'mdi-shield-account',
            colorClass: 'text-success',
            iconBgClass: 'bg-subtle',
            route: '/maker'
          }
        ] : []),
        {
          id: 'ent-shop',
          title: isId ? 'Toko Hadiah Karyawan (Reward Shop)' : 'Employee Reward Shop Catalog',
          subtitle: isId ? 'Katalog merchandise, voucher, dan reward gold' : 'Merchandise, vouchers, and gold rewards catalog',
          category: isId ? 'Hadiah' : 'Rewards',
          icon: 'mdi-wallet-giftcard',
          colorClass: 'text-danger',
          iconBgClass: 'bg-subtle',
          route: '/redeem'
        },
        {
          id: 'ent-realms',
          title: isId ? 'Marketplace Dunia Virtual 3D' : 'Virtual Realms 3D Marketplace',
          subtitle: isId ? 'Pilih tema dunia virtual 3D untuk kantor digital tim' : 'Explore and activate 3D virtual realms for your workspace',
          category: isId ? 'Gamifikasi' : 'Gamification',
          icon: 'mdi-gamepad-variant',
          colorClass: 'text-warning',
          iconBgClass: 'bg-subtle',
          route: '/realms/marketplace'
        },
        {
          id: 'ent-settings',
          title: isId ? 'Pengaturan Level & Gamifikasi' : 'Game Settings & Leveling Matrix',
          subtitle: isId ? 'Konfigurasi ambang batas XP, gelar level, dan konversi gold' : 'Configure XP thresholds, level ranks, and gold economy',
          category: isId ? 'Pengaturan' : 'Settings',
          icon: 'mdi-cogs',
          colorClass: 'text-warning',
          iconBgClass: 'bg-subtle',
          route: '/game-settings'
        },
        {
          id: 'ent-glossary',
          title: isId ? 'Glosarium & Panduan Gamifikasi' : 'Gamification Glossary & Guide',
          subtitle: isId ? 'Kamus istilah RPG dan metrik HR korporat' : 'RPG terminology dictionary and corporate HR metrics',
          category: isId ? 'Panduan' : 'Guide',
          icon: 'mdi-book-open-page-variant',
          colorClass: 'text-info',
          iconBgClass: 'bg-subtle',
          route: '/glossary'
        },
        ...(this.isOwner && !this.isMaker ? [
          {
            id: 'ent-billing',
            title: isId ? 'Tagihan & Langganan Perusahaan' : 'Company Subscription & Billing',
            subtitle: isId ? 'Cek penggunaan kuota pemain dan invoice langganan' : 'Check seat quota usage and billing invoices',
            category: isId ? 'Keuangan' : 'Billing',
            icon: 'mdi-credit-card-outline',
            colorClass: 'text-light',
            iconBgClass: 'bg-subtle',
            route: '/billing'
          }
        ] : [])
      ];
      return items;
    },
    filteredItems() {
      if (!this.query.trim()) return this.allItems;
      const q = this.query.toLowerCase();
      return this.allItems.filter(item =>
        item.title.toLowerCase().includes(q) ||
        item.subtitle.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
      );
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.query = '';
        this.activeIndex = 0;
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    move(delta) {
      const total = this.filteredItems.length;
      if (total === 0) return;
      this.activeIndex = (this.activeIndex + delta + total) % total;
    },
    selectCurrent() {
      const item = this.filteredItems[this.activeIndex];
      if (item) {
        this.executeItem(item);
      }
    },
    executeItem(item) {
      this.close();
      if (item.action) {
        item.action();
      } else if (item.route) {
        if (this.$route.path !== item.route) {
          this.$router.push(item.route);
        }
      }
    }
  }
};
</script>

<style scoped>
.cmd-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
  animation: fadeIn 0.15s ease;
}

.cmd-palette {
  background: linear-gradient(180deg, #0c2b59 0%, #071936 100%);
  border: 3px solid #7dd3fc;
  border-radius: 12px;
  width: 580px;
  max-width: 92vw;
  box-shadow: 0 0 0 2px #030a1a, 0 25px 50px -12px rgba(0, 0, 0, 0.85);
  overflow: hidden;
  animation: slideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.cmd-input-wrapper {
  display: flex;
  align-items: center;
  padding: 0.95rem 1.25rem;
  border-bottom: 2px solid #25538f;
  background: #040d21;
}

.cmd-search-icon {
  font-size: 20px;
  color: var(--text-muted);
  margin-right: 0.75rem;
}

.cmd-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: inherit;
}

.cmd-input::placeholder {
  color: var(--text-muted);
}

.cmd-kbd {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
}

.cmd-results {
  max-height: 380px;
  overflow-y: auto;
  padding: 0.5rem;
}

.cmd-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 120ms ease;
  margin-bottom: 2px;
}

.cmd-item.is-active,
.cmd-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.cmd-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  border: 1px solid var(--border-subtle);
}

.cmd-empty {
  padding: 2.5rem;
  text-align: center;
  font-size: 0.875rem;
}

.cmd-footer {
  padding: 0.65rem 1.25rem;
  border-top: 1px solid var(--border-subtle);
  background: #0a0b10;
}

.cmd-mini-kbd {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 10px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
